import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, doc, deleteDoc, updateDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import DuplicateQuestionChecker from './DuplicateQuestionChecker';
import QuestionFormatter from '../common/QuestionFormatter';

function UnitQuizManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('text'); // 'text' or 'id'
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  const [newQuestion, setNewQuestion] = useState({
    question: '',
    options: ['', '', '', ''],
    correctOption: 0,
    subject: '',
    topic: '',
    difficulty: 'beginner',
  });
  const [questionsToAdd, setQuestionsToAdd] = useState([]);
  const [manualInputError, setManualInputError] = useState(null);
  const [manualInputSuccess, setManualInputSuccess] = useState(false);
  const [manualInputLoading, setManualInputLoading] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const questionsPerPage = 5;
  const [pageInput, setPageInput] = useState('1');

  // Calculate questions to display on current page
  const indexOfLastQuestion = currentPage * questionsPerPage;
  const indexOfFirstQuestion = indexOfLastQuestion - questionsPerPage;
  const currentQuestions = filteredQuestions.slice(indexOfFirstQuestion, indexOfLastQuestion);

  // Calculate total pages
  const totalPages = Math.ceil(filteredQuestions.length / questionsPerPage);

  // Change page
  const paginate = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      setPageInput(pageNumber.toString());
    }
  };

  const handlePageInputChange = (e) => {
    setPageInput(e.target.value);
  };

  const handleJumpToPage = () => {
    const pageNumber = parseInt(pageInput, 10);
    if (!isNaN(pageNumber)) {
      paginate(pageNumber);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const questionsCollection = collection(db, 'questions');
        const questionsSnapshot = await getDocs(questionsCollection);
        const questionsList = questionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setQuestions(questionsList);
        setFilteredQuestions(questionsList);

        // Extract unique subjects and topics
        const uniqueSubjects = [...new Set(questionsList.map(q => q.subject))];
        const uniqueTopics = [...new Set(questionsList.map(q => q.topic))];
        setSubjects(uniqueSubjects);
        setTopics(uniqueTopics);

      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestions();
  }, []); // Fetch questions only on component mount

  useEffect(() => {
    let updatedFilteredQuestions = [...questions];

    // Filter by search term
    if (searchTerm) {
      if (searchType === 'text') {
        updatedFilteredQuestions = updatedFilteredQuestions.filter(q =>
          q.question.toLowerCase().includes(searchTerm.toLowerCase())
        );
      } else if (searchType === 'id') {
        updatedFilteredQuestions = updatedFilteredQuestions.filter(q =>
          q.id.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    }

    // Filter by subject
    if (filterSubject !== 'all') {
      updatedFilteredQuestions = updatedFilteredQuestions.filter(q => q.subject === filterSubject);
    }

    // Filter by topic
    if (filterTopic !== 'all') {
      updatedFilteredQuestions = updatedFilteredQuestions.filter(q => q.topic === filterTopic);
    }

    // Filter by difficulty
    if (filterDifficulty !== 'all') {
      updatedFilteredQuestions = updatedFilteredQuestions.filter(q =>
        q.difficulty.toLowerCase() === filterDifficulty.toLowerCase()
      );
    }

    setFilteredQuestions(updatedFilteredQuestions);
  }, [searchTerm, searchType, filterSubject, filterTopic, filterDifficulty, questions]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
    setPageInput('1');
  }, [searchTerm, filterSubject, filterTopic, filterDifficulty]);

  const handleDeleteQuestion = async (questionId) => {
    try {
      await deleteDoc(doc(db, 'questions', questionId));
      // Update state to reflect deletion
      setQuestions(questions.filter(q => q.id !== questionId));
    } catch (error) {
      console.error("Error deleting question:", error);
    }
  };

  const handleEditQuestion = (question) => {
    setEditingQuestion({ ...question, options: [...question.options] }); // Create a copy of options array
    setShowEditModal(true);
  };

  const handleSaveEdit = async (updatedQuestion) => {
    try {
      await updateDoc(doc(db, 'questions', updatedQuestion.id), updatedQuestion);
      // Update state to reflect the edit
      setQuestions(questions.map(q => q.id === updatedQuestion.id ? updatedQuestion : q));
      setShowEditModal(false);
      setEditingQuestion(null);
    } catch (error) {
      console.error("Error saving question edit:", error);
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingQuestion(null);
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...editingQuestion.options];
    newOptions[index] = value;
    setEditingQuestion({ ...editingQuestion, options: newOptions });
  };

  const handleAddOption = () => {
    if (editingQuestion.options.length < 4) {
      setEditingQuestion({ ...editingQuestion, options: [...editingQuestion.options, ''] });
    }
  };

  const handleRemoveOption = (index) => {
    if (editingQuestion.options.length > 1) {
      const newOptions = editingQuestion.options.filter((_, i) => i !== index);
      setEditingQuestion({ ...editingQuestion, options: newOptions });
    }
  };

  const handleNewQuestionInputChange = (e) => {
    const { name, value } = e.target;
    setNewQuestion({ ...newQuestion, [name]: value });
  };

  const handleNewOptionChange = (index, value) => {
    const options = [...newQuestion.options];
    options[index] = value;
    setNewQuestion({ ...newQuestion, options });
  };

  const handleAddNewQuestion = () => {
    // Basic validation
    if (!newQuestion.question || newQuestion.options.some(opt => opt === '') || newQuestion.subject === '') {
      setManualInputError("Please fill in all fields for the question and options.");
      return;
    }
    if (questionsToAdd.length >= 10) {
      setManualInputError("You can only add up to 10 questions at a time.");
      return;
    }
    setQuestionsToAdd([...questionsToAdd, newQuestion]);
    setNewQuestion({
      question: '',
      options: ['', '', '', ''],
      correctOption: 0,
      subject: '',
      topic: '',
      difficulty: 'beginner',
    });
    setManualInputError(null);
    setManualInputSuccess(false);
  };

  const handleBatchUpload = async () => {
    if (questionsToAdd.length === 0) {
      setManualInputError("No questions to upload.");
      return;
    }

    setManualInputLoading(true);
    setManualInputError(null);
    setManualInputSuccess(false);

    try {
      const batch = writeBatch(db);
      const questionsRef = collection(db, 'questions');

      questionsToAdd.forEach(question => {
        const newQuestionRef = doc(questionsRef);
        batch.set(newQuestionRef, {
          ...question,
          correctOption: parseInt(question.correctOption, 10), // Ensure correctOption is a number
          createdAt: serverTimestamp(),
          // Add set title/description if needed, or remove if only for file upload
        });
      });

      await batch.commit();

      setQuestionsToAdd([]);
      setManualInputSuccess(`Successfully added ${questionsToAdd.length} questions.`);
      // Refresh questions list after upload
      // This will trigger the useEffect for filtering as 'questions' state changes
      const questionsCollection = collection(db, 'questions');
      const questionsSnapshot = await getDocs(questionsCollection);
      const questionsList = questionsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setQuestions(questionsList);

    } catch (error) {
      console.error("Error adding batch questions:", error);
      setManualInputError("Failed to add questions. Please try again.");
    } finally {
      setManualInputLoading(false);
    }
  };

  const handleRemoveFromBatch = (index) => {
    const updatedBatch = questionsToAdd.filter((_, i) => i !== index);
    setQuestionsToAdd(updatedBatch);
  };

  return (
    <div className="space-y-8">
      <section id="unit-quiz-management" className="bg-white p-6 rounded-lg shadow-lg">
        <h2 className="text-2xl font-semibold mb-4">Individual Quiz Management</h2>

        {/* Add DuplicateQuestionChecker component */}
        <div className="mb-8">
          <DuplicateQuestionChecker />
        </div>

        <div className="mb-4 flex items-center gap-4">
          <div className="flex-1 flex gap-2">
            <select
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
            >
              <option value="text">Search by Question Text</option>
              <option value="id">Search by Question ID</option>
            </select>
            <input
              type="text"
              placeholder={searchType === 'text' ? "Search question text..." : "Search question ID..."}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof w-full"
            />
          </div>

          <select
            value={filterSubject}
            onChange={(e) => setFilterSubject(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          >
            <option value="all">All Subjects</option>
            {subjects.map(subject => (
              <option key={subject} value={subject}>{subject}</option>
            ))}
          </select>

          <select
            value={filterTopic}
            onChange={(e) => setFilterTopic(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          >
            <option value="all">All Topics</option>
            {topics.map(topic => (
              <option key={topic} value={topic}>{topic}</option>
            ))}
          </select>

          <select
            value={filterDifficulty}
            onChange={(e) => setFilterDifficulty(e.target.value)}
            className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
          >
            <option value="all">All Difficulties</option>
            <option value="beginner">Beginner</option>
            <option value="intermediate">Intermediate</option>
            <option value="advanced">Advanced</option>
          </select>
        </div>

        {/* Display Filtered Questions */}
        <div className="space-y-4">
          {currentQuestions.map(question => (
            <div key={question.id} className="bg-gray-100 p-4 rounded-lg shadow">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-semibold">{question.question}</p>
                  <p className="text-sm text-gray-600">
                    ID: {question.id} | Subject: {question.subject} | 
                    Topic: {question.topic} | Difficulty: {question.difficulty}
                  </p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditQuestion(question)}
                    className="text-sm bg-blue-500 text-white py-1 px-3 rounded hover:bg-blue-600"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteQuestion(question.id)}
                    className="text-sm bg-red-500 text-white py-1 px-3 rounded hover:bg-red-600"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination Controls */}
        {filteredQuestions.length > questionsPerPage && (
          <div className="flex justify-center mt-4 space-x-2">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <div className="flex items-center gap-1">
              <span>Page</span>
              <input
                type="number"
                min="1"
                max={totalPages}
                value={pageInput}
                onChange={handlePageInputChange}
                className="w-12 px-1 py-1 border rounded text-center"
              />
              <span>of {totalPages}</span>
              <button
                onClick={handleJumpToPage}
                className="px-3 py-1 border rounded bg-iof text-white hover:bg-iof-dark"
              >
                Go
              </button>
            </div>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}

        {/* Manual Input Section */}
        <div className="mt-8 border-t pt-8">
          <h3 className="text-xl font-semibold mb-4">Manually Add Questions (Batch of 10)</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Question Text</label>
              <textarea
                name="question"
                value={newQuestion.question}
                onChange={handleNewQuestionInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm" rows="2"
              ></textarea>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Options</label>
              {newQuestion.options.map((option, index) => (
                <input
                  key={index}
                  type="text"
                  placeholder={`Option ${index + 1}`}
                  value={option}
                  onChange={(e) => handleNewOptionChange(index, e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              ))}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Correct Option (0-indexed)</label>
              <input
                type="number"
                name="correctOption"
                value={newQuestion.correctOption}
                onChange={handleNewQuestionInputChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                min="0"
                max="3"
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Subject</label>
                <input
                  type="text"
                  name="subject"
                  value={newQuestion.subject}
                  onChange={handleNewQuestionInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Topic</label>
                <input
                  type="text"
                  name="topic"
                  value={newQuestion.topic}
                  onChange={handleNewQuestionInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Difficulty</label>
                <select
                  name="difficulty"
                  value={newQuestion.difficulty}
                  onChange={handleNewQuestionInputChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
            </div>
            <button
              onClick={handleAddNewQuestion}
              className="bg-green-500 text-white py-2 px-4 rounded-md hover:bg-green-600 transition-colors"
              disabled={questionsToAdd.length >= 10}
            >
              Add to Batch ({questionsToAdd.length}/10)
            </button>
          </div>

          {questionsToAdd.length > 0 && (
            <div className="mt-4">
              <h4 className="text-lg font-semibold mb-2">Questions in Batch:</h4>
              <ul className="list-disc list-inside space-y-1">
                {questionsToAdd.map((q, index) => (
                  <li key={index} className="text-sm flex justify-between items-center">
                    {q.question}
                    <button onClick={() => handleRemoveFromBatch(index)} className="text-red-500 hover:text-red-700 text-xs ml-2">Remove</button>
                  </li>
                ))}
              </ul>
              <button
                onClick={handleBatchUpload}
                className={`mt-4 py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white ${manualInputLoading ? 'bg-gray-400' : 'bg-iof hover:bg-iof-dark'}`}
                disabled={manualInputLoading}
              >
                {manualInputLoading ? 'Uploading Batch...' : `Upload Batch (${questionsToAdd.length})`}
              </button>
            </div>
          )}

          {manualInputError && (
            <div className="text-red-500 text-sm mt-2">{manualInputError}</div>
          )}
          {manualInputSuccess && (
            <div className="text-green-500 text-sm mt-2">{manualInputSuccess}</div>
          )}

        </div>

      </section>

      {/* Edit Question Modal */}
      {showEditModal && editingQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Edit Question</h3>
              <button
                onClick={handleCancelEdit}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Question Text</label>
                <QuestionFormatter
                  value={editingQuestion.question}
                  onChange={(value) => setEditingQuestion({ ...editingQuestion, question: value })}
                  preview={true}
                  showGuide={true}
                />
              </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
                <div className="space-y-4">
              {editingQuestion.options.map((option, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center mb-2">
                        <label className="text-sm text-gray-600">Option {index + 1}</label>
                        {editingQuestion.options.length > 1 && (
                          <button
                            onClick={() => handleRemoveOption(index)}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove Option
                          </button>
                        )}
                      </div>
                      <QuestionFormatter
                    value={option}
                        onChange={(value) => handleOptionChange(index, value)}
                        preview={true}
                        showGuide={false}
                  />
                    </div>
                  ))}
                  {editingQuestion.options.length < 4 && (
                    <button
                      onClick={handleAddOption}
                      className="text-blue-500 hover:text-blue-700 text-sm flex items-center"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Option
                    </button>
                  )}
                </div>
            </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Correct Option</label>
                  <select
                value={editingQuestion.correctOption}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, correctOption: parseInt(e.target.value, 10) })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                  >
                    {editingQuestion.options.map((_, index) => (
                      <option key={index} value={index}>Option {index + 1}</option>
                    ))}
                  </select>
            </div>

            <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Subject</label>
              <input
                type="text"
                value={editingQuestion.subject}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, subject: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              />
            </div>

            <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Topic</label>
              <input
                type="text"
                value={editingQuestion.topic}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, topic: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              />
            </div>

            <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Difficulty</label>
              <select
                value={editingQuestion.difficulty}
                onChange={(e) => setEditingQuestion({ ...editingQuestion, difficulty: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>
              </div>

              <div className="flex justify-end gap-4 pt-4 border-t">
                <button
                  onClick={handleCancelEdit}
                  className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleSaveEdit(editingQuestion)}
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UnitQuizManagement;
